import { Injectable, Type } from '@angular/core';
import { ActivationEnd } from '@angular/router';

import { LayoutNavigationConfig, SitemapSitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { SlotComponentOptions, SlotLayoutService } from '@frontend/sports/layout/feature';
import { TopNavigationVersion } from '@frontend/sports/navigation/core/feature/models';
import { CleanNavigationComponentVisibilityService, NavigationService } from '@frontend/sports/web/navigation-data-access';
import { NavigationComponent } from '@frontend/sports/web/navigation-lib';
import { OnAppInit, injectLazy } from '@frontend/vanilla/core';
import { combineLatest, switchMap, tap } from 'rxjs';

import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { SubNavigationComponent } from '../sub-navigation/sub-navigation.component';
import { SitemapModuleLoaderService } from './sitemap-module-loader.service';

@Injectable({
    providedIn: 'root',
})
export class SlotLayoutRouteOverrideService implements OnAppInit {
    private initialSettings?: SlotComponentOptions;
    private component: Type<any>;
    private sitemapBootstrapService$ = injectLazy(() => import('../sitemap/sitemap.bootstrap.service').then((m) => m.SitemapBootstrapService));

    constructor(
        private sitecore: SitemapSitecore,
        private routerEvents: RouterEventsService,
        private slotLayout: SlotLayoutService,
        private layoutNavConfig: LayoutNavigationConfig,
        private sitemapModuleLoaderService: SitemapModuleLoaderService,
        private navService: NavigationService,
        private componentVisibilityService: CleanNavigationComponentVisibilityService,
        private productUtils: SportsProductUtils,
    ) {}

    onAppInit(): void | Promise<any> {
        const topNavV2 = this.layoutNavConfig.topNavigationVersion === TopNavigationVersion.V2;
        const topNavV3 = this.layoutNavConfig.topNavigationVersion === TopNavigationVersion.V3;

        this.slotLayout.setup();

        const renderSubNavigation$ = combineLatest([
            this.routerEvents.currentActivationEnd.pipe(tap((e) => this.routeChanged(e))),
            this.componentVisibilityService.subNavigationVisibility$.pipe(
                this.productUtils.filterSports(),
                tap((visible) => this.setSubNavigationVisibility(visible)),
            ),
        ]);

        // it's important to keep the order of inserting in line with the defined order, otherwise we'll
        // run into hydration issues coming from SSR

        if (topNavV2) {
            this.navService.init();
            this.slotLayout.register(NavigationComponent, { order: 0, fixed: true });
            this.slotLayout.register(BreadcrumbsComponent, { order: 10 });
            renderSubNavigation$.subscribe();
        } else if (topNavV3) {
            combineLatest([this.sitemapModuleLoaderService.loadAndInitModule(), this.sitemapBootstrapService$, this.sitecore.whenReady])
                .pipe(
                    tap(([module, service]) => {
                        service.resolversMap = module.sitemapResolversMap;
                        service.onAppInit();
                        this.slotLayout.register(module.sitemapComponent, { order: 0, fixed: true });
                        this.slotLayout.register(BreadcrumbsComponent, { order: 10 });
                    }),
                    switchMap(() => renderSubNavigation$),
                )
                .subscribe();
        } else {
            this.slotLayout.register(BreadcrumbsComponent, { order: 10 });
        }
    }

    private routeChanged(event?: ActivationEnd): void {
        if (!event) {
            return;
        }

        if (event.snapshot.data?.tag === RouteTag.HorseRaceDetails) {
            this.override(SubNavigationComponent, { fixed: false });
            return;
        }

        this.destroy();
    }

    override(component: Type<any>, options: SlotComponentOptions): void {
        if (this.initialSettings) {
            return;
        }

        this.component = component;
        this.initialSettings = this.slotLayout.getConfig(component);

        if (!this.initialSettings) {
            throw new Error(`Could not override component ${component} - as it was not registered`);
        }

        this.slotLayout.override(component, options);
    }

    private destroy(): void {
        if (this.initialSettings) {
            this.slotLayout.override(this.component, this.initialSettings);
            this.initialSettings = undefined;
        }
    }

    private setSubNavigationVisibility(visible: boolean) {
        const subNavigationVisible = !!this.slotLayout.getConfig(SubNavigationComponent);
        if (subNavigationVisible === visible) {
            return;
        }

        if (!visible) {
            this.slotLayout.remove(SubNavigationComponent);
            return;
        }

        this.slotLayout.register(SubNavigationComponent, { order: 100, fixed: true });
    }
}
